/**
 * water_rules.ts — WHERE THE WATER IS, one rule for both sides (2026-09-28, "fix the fishing to work better, and fix
 * invite spots"). MIRRORED byte-for-byte: shared/water_rules.ts (the client) ≡ server/src/lib/water_rules.ts (the
 * module); `verify:water-index` compares the files. No imports: the ground, the plateaus, the river test and the baked
 * lakes come in as arguments, so each side supplies its own and the answer is the same.
 *
 * The sea was the only water the server could see; a `castLine({ water: 'lake' })` anywhere on dry land was believed,
 * and an invitee could be stood in a river. The server now judges; the CLIENT asks the same rule before it offers a
 * cast (on the same f32 coordinates the wire carries and the same river-free ground — terrainHeightNoRivers), so an
 * honest cast is never refused. Measured 2026-09-28 against the client's own lake bake (lakes.ts): a server-only
 * check with no client mirror would have refused 5–11 % of rendered pond water in rugged regions.
 *
 *   SEA      ground under the sea level − SEA_CAST_FREEBOARD_M (the rendered sea also covers shallows).
 *   RIVER    the caller's test (the server's baked table, within a segment's half width + slack).
 *   LAKE     a baked lake's box (+ one bake cell) whose level stands over the ground (+ slack), else
 *   POND     lakes.ts's own pond rule at 10 m: a PRIORITY-FLOOD (Barnes 2014) over a POND_N² grid of POND_STEP_M cells
 *            around the point, drained at the window's border; a node is wet where the fill stands POND_MIN_DEPTH_M over
 *            its ground (MIN_DEPTH); wet nodes form basins (4-neighbour); a basin deeper than POND_MAX_DEPTH_M is DROPPED
 *            whole (MAX_COMP_DEPTH — a rim-locked valley is not a pond; capping it instead flooded ~half of all land),
 *            as is one under POND_MIN_NODES (MIN_CELLS) or at the sea (SEA_MARGIN_M); the point is water when its
 *            nearest node is in a kept basin. THE CLAIMED LEVEL first: the client renders a pond's surface, so a cast
 *            carries that level and the rule checks a basin holds water AT it around the point (`pondAtLevel`: the
 *            nodes under the level connected to the point stay inside POND_REACH_NODES — the water does not spill —
 *            and the basin obeys the same depth, size and sea rules). A windowed flood holds only ~73–80 % of rendered
 *            pond water (its drained border cuts basins the client's 1.5 km window keeps); the claimed level holds
 *            nearly all of it, and a false level on dry land spills or is too deep. Windows snap to a lattice and are
 *            cached per seed, so
 *            a point costs one ground sample once its window is flooded (a ray pre-filter was tried and dropped: it ran
 *            per POINT, ~5 ms each on the client's cast search, and refused the shallow margins the flood holds).
 *   DRY      a settlement's plateau is never water (lakes.ts PLATEAU_DRY_M).
 *
 * THE DRAWN POND (2026-10-01, the owner: "fix the 7% pond points that show no rendered water"). A BUILDING is judged by
 * a second pond rule, `pondStands` — the flood above stays the fishing cast's and the invite spot's, untouched. Measured
 * on ten regions (77,499 land points) the flood called 8,174 of them pond and the client drew no water at or beside
 * 24 % of those, for three reasons, in this order: its 320 m WINDOW (a basin the client's 1.5 km lake region sees whole
 * — deeper than POND_MAX_DEPTH_M, so dropped — is a shallow pond to a window that cuts it), its GROUND (the client
 * floods the terrain it draws: river channels cut in, beds raised across hollows, a flowing channel standing at its
 * water) and its 4-neighbour fill. The new rule asks the client's question on the client's ground:
 *   the bed     the caller's `bed` — the DRAWN ground (the river carve included) with a flowing channel at its water
 *               (`pondBedLevel`, lakes.ts's own cap). The server registers the river network for this alone
 *               (water_index.ts; nothing else on the server reads a channel height).
 *   the level   a best-first search from the point's 10 m node outward (8-neighbour, the key = the highest bed on the
 *               way: the minimax path, so each time the key rises the nodes popped so far ARE the basin under it).
 *               Leaving the NEAR box (POND_NEAR_NODES) gives the level a client whose drained border stands that close
 *               would still fill to — the lowest any client draws; leaving the FAR box (POND_FAR_NODES) gives the
 *               highest. A client's level depends on where its region is centred, so no single flood can match it:
 *   the verdict pond only where EVERY client draws one — the water stands POND_MIN_DEPTH_M + POND_SHORE_MARGIN_M over
 *               the point's own bed (and its node's) at the NEAR level, and at the FAR level the basin still holds:
 *               not cut by the far box, not deeper than POND_MAX_DEPTH_M, POND_MIN_NODES wet nodes, above the sea.
 * Equal keys pop FARTHEST first (any tie order gives the same levels and the same basin; this one walks out along the
 * drain instead of filling every hollow below the level — a third of the ground samples). Measured on the same ten
 * regions: pond with no drawn water 24.32 % → 0.20 % (12 of 6,098); drawn pond water the rule calls dry 38.12 % → 33.24 %
 * (the rule is the conservative side of every client, so that half is the price of the first — and an honest client
 * refuses drawn water itself, placement_rules.ts `waterAt`). `verify:pond-parity` is the gate.
 */
export type Water = 'sea' | 'lake' | 'river';
export type Height = (x: number, z: number) => number;
export type Dry = ((x: number, z: number) => boolean) | null;
/** The river test: is (x, z) within a channel's half width + slack? */
export type RiverTest = (x: number, z: number, slack: number) => boolean;

/** Rivers: a cast within a segment's half width plus this (the ribbon's lip, the table's rounding, a bank). */
export const RIVER_CAST_SLACK_M = 4;
/** Standing in a river: its own channel, rounded. */
export const RIVER_STAND_SLACK_M = 0.5;
/** Lakes: the box grows by one bake cell (the regional lake grows to connected cells past the 64 m box). */
export const LAKE_BOX_MARGIN_M = 64;
/** Lakes: the ground may stand this far above the level (the rendered ground is the chunk mesh). */
export const LAKE_LEVEL_SLACK_M = 0.3;
/** The sea's freeboard for a cast (the rendered sea also covers shallows; the fish want real water). */
export const SEA_CAST_FREEBOARD_M = 0.3;
/** The pond flood: a 33² grid of 10 m cells (a 320 m window). */
export const POND_N = 33;
export const POND_STEP_M = 10;
/** Windows snap to this lattice (the point stays ≥ 80 m from the drained border). */
export const POND_SNAP_M = 160;
/** A node is wet where the fill stands this far over its ground (lakes.ts MIN_DEPTH). */
export const POND_MIN_DEPTH_M = 0.35;
/** A basin deeper than this is dropped whole (lakes.ts MAX_COMP_DEPTH). */
export const POND_MAX_DEPTH_M = 5;
/** A basin needs this many 10 m nodes (lakes.ts MIN_CELLS: 6 cells of 6 m = 216 m²). */
export const POND_MIN_NODES = 3;
/** A basin whose surface is this close to the sea belongs to the sea (lakes.ts SEA_MARGIN_M). */
export const POND_SEA_MARGIN_M = 2;
/** A claimed level's basin must close within this many 10 m nodes of the point (a larger lake is a baked lake). */
export const POND_REACH_NODES = 24;

/** A baked lake's level where its water covers the point, else null. `lakes` = flat [x0, z0, x1, z1, level × 10]. */
export function bakedLakeLevel(lakes: ArrayLike<number>, x: number, z: number, ground: number, slack = LAKE_LEVEL_SLACK_M): number | null {
  if (!Number.isFinite(ground)) return null;
  const m = LAKE_BOX_MARGIN_M;
  for (let i = 0; i + 4 < lakes.length; i += 5) {
    if (x < lakes[i] - m || x > lakes[i + 2] + m || z < lakes[i + 1] - m || z > lakes[i + 3] + m) continue;
    const level = lakes[i + 4] / 10;
    if (ground < level + slack) return level;
  }
  return null;
}

interface PondWindow { x0: number; z0: number; keep: Uint8Array }
const pondCache = new Map<string, PondWindow>();
const POND_CACHE_MAX = 48;
/** Ground samples on the 10 m node lattice, shared by overlapping windows (a node lies in up to four): a walk along a
 *  shore floods its next window from mostly cached ground. Cleared whole past its cap (the answer never depends on it). */
const nodeGround = new Map<string, Map<number, number>>();
const NODE_GROUND_MAX = 60000;
function groundAt(tag: string, height: Height, ix: number, iz: number): number {
  let m = nodeGround.get(tag);
  if (!m) { m = new Map(); nodeGround.set(tag, m); }
  const k = (ix + 1048576) * 2097152 + (iz + 1048576);
  let v = m.get(k);
  if (v === undefined) {
    if (m.size >= NODE_GROUND_MAX) m.clear();
    v = height(ix * POND_STEP_M, iz * POND_STEP_M);
    m.set(k, v);
  }
  return v;
}
const NB: ReadonlyArray<readonly [number, number]> = [[1, 0], [-1, 0], [0, 1], [0, -1]];
const NB8: ReadonlyArray<readonly [number, number]> = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];

/** A flooded window's kept pond nodes (cached per tag, sea and snapped centre; LRU by re-insertion). */
function pondWindow(tag: string, height: Height, seaLevel: number | null, x: number, z: number): PondWindow {
  const cx = Math.round(x / POND_SNAP_M) * POND_SNAP_M, cz = Math.round(z / POND_SNAP_M) * POND_SNAP_M;
  const key = `${tag}|${seaLevel ?? ''}|${cx}|${cz}`;
  const hit = pondCache.get(key);
  if (hit) { pondCache.delete(key); pondCache.set(key, hit); return hit; }
  const n = POND_N, half = ((n - 1) / 2) * POND_STEP_M, x0 = cx - half, z0 = cz - half;
  const h = new Float64Array(n * n), level = new Float64Array(n * n).fill(Infinity);
  const ix0 = Math.round(x0 / POND_STEP_M), iz0 = Math.round(z0 / POND_STEP_M); // the window's corner on the node lattice
  for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) { const v = groundAt(tag, height, ix0 + i, iz0 + j); h[j * n + i] = Number.isFinite(v) ? v : -1e9; }
  // Priority-flood from the border: a border node drains (its level is its own ground).
  const heap: number[] = [];
  const less = (a: number, b: number): boolean => level[a] < level[b] || (level[a] === level[b] && a < b);
  const push = (id: number): void => { heap.push(id); let c = heap.length - 1; while (c > 0) { const p = (c - 1) >> 1; if (!less(heap[c], heap[p])) break; const t = heap[p]; heap[p] = heap[c]; heap[c] = t; c = p; } };
  const pop = (): number => {
    const top = heap[0], last = heap.pop() as number;
    if (heap.length) {
      heap[0] = last;
      let c = 0;
      for (;;) { const l = 2 * c + 1, r = l + 1; let m = c; if (l < heap.length && less(heap[l], heap[m])) m = l; if (r < heap.length && less(heap[r], heap[m])) m = r; if (m === c) break; const t = heap[m]; heap[m] = heap[c]; heap[c] = t; c = m; }
    }
    return top;
  };
  for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) if (i === 0 || j === 0 || i === n - 1 || j === n - 1) { const id = j * n + i; level[id] = h[id]; push(id); }
  while (heap.length) {
    const c = pop(), ci = c % n, cj = (c - ci) / n;
    for (const [di, dj] of NB) {
      const ni = ci + di, nj = cj + dj;
      if (ni < 0 || nj < 0 || ni >= n || nj >= n) continue;
      const id = nj * n + ni;
      if (level[id] !== Infinity) continue;
      level[id] = Math.max(h[id], level[c]);
      push(id);
    }
  }
  // lakes.ts's components: wet nodes (fill − ground > MIN_DEPTH), 4-connected; a basin is kept unless it is deeper
  // than MAX_COMP_DEPTH (dropped whole), smaller than MIN_CELLS or at the sea.
  const wet = new Uint8Array(n * n);
  for (let i = 0; i < n * n; i++) if (level[i] - h[i] > POND_MIN_DEPTH_M) wet[i] = 1;
  const keep = new Uint8Array(n * n), seen = new Uint8Array(n * n);
  const comp: number[] = [];
  for (let s = 0; s < n * n; s++) {
    if (!wet[s] || seen[s]) continue;
    comp.length = 0; comp.push(s); seen[s] = 1;
    let depth = 0, surface = -Infinity;
    for (let k = 0; k < comp.length; k++) {
      const q = comp[k], qi = q % n, qj = (q - qi) / n;
      if (level[q] - h[q] > depth) depth = level[q] - h[q];
      if (level[q] > surface) surface = level[q];
      for (const [di, dj] of NB) {
        const ni = qi + di, nj = qj + dj;
        if (ni < 0 || nj < 0 || ni >= n || nj >= n) continue;
        const id = nj * n + ni;
        if (!wet[id] || seen[id]) continue;
        seen[id] = 1; comp.push(id);
      }
    }
    if (comp.length < POND_MIN_NODES || depth > POND_MAX_DEPTH_M) continue;
    if (seaLevel !== null && surface <= seaLevel + POND_SEA_MARGIN_M) continue;
    for (const q of comp) keep[q] = 1;
  }
  const win = { x0, z0, keep };
  pondCache.set(key, win);
  if (pondCache.size > POND_CACHE_MAX) { const first = pondCache.keys().next().value; if (first !== undefined) pondCache.delete(first); }
  return win;
}

/** Would the lake bake fill this point? Its nearest 10 m node is in a kept basin. `tag` = the world's seed. */
export function inPond(tag: string, height: Height, seaLevel: number | null, x: number, z: number): boolean {
  if (!Number.isFinite(x) || !Number.isFinite(z)) return false;
  const w = pondWindow(tag, height, seaLevel, x, z), n = POND_N;
  const i = Math.round((x - w.x0) / POND_STEP_M), j = Math.round((z - w.z0) / POND_STEP_M);
  if (i <= 0 || j <= 0 || i >= n - 1 || j >= n - 1) return false;
  return w.keep[j * n + i] === 1;
}

/**
 * A pond whose surface stands at `level` holds the point: its nearest node lies POND_MIN_DEPTH_M under the level, the
 * nodes under the level connected to it (4-neighbour, on the global 10 m lattice) close within POND_REACH_NODES of it
 * (the water does not spill), the basin is at most POND_MAX_DEPTH_M deep, holds POND_MIN_NODES wet nodes and stands
 * above the sea. Deterministic on its inputs: the client asks it with the level it renders, the server with the level
 * the cast carries (both as f32).
 */
export function pondAtLevel(tag: string, height: Height, seaLevel: number | null, x: number, z: number, level: number): boolean {
  if (!Number.isFinite(x) || !Number.isFinite(z) || !Number.isFinite(level)) return false;
  if (seaLevel !== null && level <= seaLevel + POND_SEA_MARGIN_M) return false;
  const i0 = Math.round(x / POND_STEP_M), j0 = Math.round(z / POND_STEP_M);
  if (!(level - groundAt(tag, height, i0, j0) > POND_MIN_DEPTH_M)) return false;
  const R = POND_REACH_NODES, side = 2 * R + 1;
  const seen = new Uint8Array(side * side);
  const stack: number[] = [R * side + R];
  seen[R * side + R] = 1;
  let lowest = Infinity, wetNodes = 0;
  while (stack.length) {
    const c = stack.pop() as number, ci = c % side, cj = (c - ci) / side;
    const g = groundAt(tag, height, i0 + ci - R, j0 + cj - R);
    if (g < lowest) lowest = g;
    if (level - g > POND_MIN_DEPTH_M) wetNodes++;
    for (const [di, dj] of NB) {
      const ni = ci + di, nj = cj + dj;
      if (ni < 0 || nj < 0 || ni >= side || nj >= side) return false; // under the level at the reach's edge: it spills
      const id = nj * side + ni;
      if (seen[id]) continue;
      if (!(groundAt(tag, height, i0 + ni - R, j0 + nj - R) < level)) continue;
      seen[id] = 1; stack.push(id);
    }
  }
  return level - lowest <= POND_MAX_DEPTH_M && wetNodes >= POND_MIN_NODES;
}

// ── THE DRAWN POND ───────────────────────────────────────────────────────────────────────────────────────────────

/** The near box, in 10 m nodes: a client's lake region is 1,536 m wide, re-centred when the camera strays 384 m, so its
 *  drained border stands ≥ 768 − 384 − (the reach of a placement cursor, ~84 m) = 300 m from a point being placed. */
export const POND_NEAR_NODES = 30;
/** The far box: the border stands at most 768 + 384 (+ the cursor) from the point — a basin is judged whole. */
export const POND_FAR_NODES = 120;
/** The water must stand this far over the renderer's own minimum depth at the point: the shore's last centimetres are
 *  where a 6 m render lattice and a 10 m rule lattice disagree (measured: 0 → 0.42 %, 0.05 → 0.27 %, 0.10 → 0.20 % of
 *  pond answers undrawn; for a client centred 410 m away on a second sample 1.02 % → 0.71 % → 0.49 %). */
export const POND_SHORE_MARGIN_M = 0.1;
/** A search that pops more nodes than this is no pond (a lake that size is a baked lake; 7 of 70,406 sampled searches
 *  reached it). It bounds a first judge on fresh ground at ~6,000 bed samples. */
export const POND_SEARCH_MAX = 4000;

/** The lake bake's bed (lakes.ts fillRows): the drawn ground, with a FLOWING channel standing at its water (a basin
 *  spills through a channel at the surface, not the bed); a still reach — on a lake, at the sea — is lake floor. */
export function pondBedLevel(ground: number, riverWater: number | null, still: boolean): number {
  return riverWater !== null && riverWater > ground && !still ? riverWater : ground;
}

/** A node's two levels: `near` (−Infinity = no pond before the near box), `far` (NaN = not searched yet, −Infinity = the
 *  basin does not hold, else the pond's level). */
interface PondLevels { near: number; far: number }
const pondLevels = new Map<string, Map<number, PondLevels>>();
const POND_LEVELS_MAX = 8192;
const PS = 2 * POND_FAR_NODES + 1;
let psKey: Float64Array | null = null, psBed: Float64Array | null = null, psSeen: Uint32Array | null = null, psPopped: Int32Array | null = null, psStamp = 0;

/** The search from node (i0, j0). `full` false stops at the near box (most points are dry there). */
function pondSearch(tag: string, bed: Height, seaLevel: number | null, i0: number, j0: number, full: boolean): PondLevels {
  if (!psKey || !psBed || !psSeen || !psPopped) { psKey = new Float64Array(PS * PS); psBed = new Float64Array(PS * PS); psSeen = new Uint32Array(PS * PS); psPopped = new Int32Array(POND_SEARCH_MAX + 1); }
  const key = psKey, gr = psBed, seen = psSeen, popped = psPopped;
  if (psStamp >= 0xfffffff0) { seen.fill(0); psStamp = 0; }
  const stamp = ++psStamp, R = POND_FAR_NODES;
  const dist = (id: number): number => { const i = id % PS, j = (id - i) / PS, a = i > R ? i - R : R - i, b = j > R ? j - R : R - j; return a > b ? a : b; };
  const heap: number[] = [];
  // lowest key first; equal keys FARTHEST first (see the header), then the lower id — a total order, the same on both sides
  const less = (a: number, b: number): boolean => { if (key[a] !== key[b]) return key[a] < key[b]; const da = dist(a), db = dist(b); return da > db || (da === db && a < b); };
  const push = (id: number): void => { heap.push(id); let c = heap.length - 1; while (c > 0) { const p = (c - 1) >> 1; if (!less(heap[c], heap[p])) break; const t = heap[p]; heap[p] = heap[c]; heap[c] = t; c = p; } };
  const pop = (): number => {
    const top = heap[0], last = heap.pop() as number;
    if (heap.length) {
      heap[0] = last;
      let c = 0;
      for (;;) { const l = 2 * c + 1, r = l + 1; let m = c; if (l < heap.length && less(heap[l], heap[m])) m = l; if (r < heap.length && less(heap[r], heap[m])) m = r; if (m === c) break; const t = heap[m]; heap[m] = heap[c]; heap[c] = t; c = m; }
    }
    return top;
  };
  const c0 = R * PS + R, g0 = groundAt(tag, bed, i0, j0);
  gr[c0] = Number.isFinite(g0) ? g0 : -1e9; key[c0] = gr[c0]; seen[c0] = stamp; push(c0);
  let K = -Infinity, lowest = Infinity, ev = 0, n = 0, near = NaN, far = -Infinity;
  while (heap.length) {
    const q = pop(), k = key[q], d = dist(q);
    if (Number.isNaN(near) && d >= POND_NEAR_NODES) { near = k; if (!full) return { near, far: NaN }; }
    if (d >= R) { if (!(k > K)) far = K; break; }               // out at the level it stands at; a rise AT the box = a basin the box cuts
    if (k > K) { ev = n; K = k; if (K - lowest > POND_MAX_DEPTH_M) break; } // the nodes popped before a rise are the basin under it
    if (gr[q] < lowest) lowest = gr[q];
    popped[n++] = q;
    if (n > POND_SEARCH_MAX) break;
    const qi = q % PS, qj = (q - qi) / PS;
    for (const [di, dj] of NB8) {
      const id = (qj + dj) * PS + qi + di; // inside the grid: q is short of the far box
      if (seen[id] === stamp) continue;
      seen[id] = stamp;
      const g = groundAt(tag, bed, i0 + qi + di - R, j0 + qj + dj - R);
      gr[id] = Number.isFinite(g) ? g : -1e9;
      key[id] = gr[id] > k ? gr[id] : k;
      push(id);
    }
  }
  if (Number.isNaN(near)) near = -Infinity; // it ended before the near box: a basin too deep to be a pond
  if (far !== -Infinity) {
    let wet = 0;
    for (let i = 0; i < ev; i++) if (far - gr[popped[i]] > POND_MIN_DEPTH_M) wet++;
    if (wet < POND_MIN_NODES || (seaLevel !== null && far <= seaLevel + POND_SEA_MARGIN_M)) far = -Infinity;
  }
  return { near, far };
}

/**
 * Does EVERY client draw pond water over the point? (THE DRAWN POND, the header.) `bed` = the drawn bed (the carved
 * ground through `pondBedLevel`), `tag` = a name for that bed (the caches' key: one bed per tag), `seaLevel` null = no
 * sea rule. Deterministic on its inputs; a node's levels are cached, so a second point by the same node costs one bed
 * sample.
 */
export function pondStands(tag: string, bed: Height, seaLevel: number | null, x: number, z: number): boolean {
  if (!Number.isFinite(x) || !Number.isFinite(z)) return false;
  const i0 = Math.round(x / POND_STEP_M), j0 = Math.round(z / POND_STEP_M);
  const here = bed(x, z), node = groundAt(tag, bed, i0, j0);
  if (!Number.isFinite(here) || !Number.isFinite(node)) return false;
  const floor = here > node ? here : node, need = POND_MIN_DEPTH_M + POND_SHORE_MARGIN_M;
  const ck = `${tag}|${seaLevel ?? ''}`;
  let m = pondLevels.get(ck);
  if (!m) { m = new Map(); pondLevels.set(ck, m); }
  const k = (i0 + 1048576) * 2097152 + (j0 + 1048576);
  let e = m.get(k);
  if (!e) {
    if (m.size >= POND_LEVELS_MAX) m.clear();
    e = pondSearch(tag, bed, seaLevel, i0, j0, false);
    m.set(k, e);
  }
  if (!(e.near - floor > need)) return false;
  if (Number.isNaN(e.far)) e.far = pondSearch(tag, bed, seaLevel, i0, j0, true).far;
  return e.far - floor > need;
}

/** The sea at the point. */
export function seaWater(height: Height, seaLevel: number, x: number, z: number): boolean {
  return Number.isFinite(x) && Number.isFinite(z) && height(x, z) < seaLevel - SEA_CAST_FREEBOARD_M;
}

/** A lake at the point: a baked lake over its ground, else a pond at the claimed level, else a pond the flood finds —
 *  never the sea, never a plateau. `seaLevel` null = a world without a sea rule; `level` NaN = no level claimed. */
export function lakeWater(lakes: ArrayLike<number>, tag: string, height: Height, seaLevel: number | null, x: number, z: number, dry: Dry = null, level = NaN): boolean {
  if (!Number.isFinite(x) || !Number.isFinite(z)) return false;
  const g = height(x, z);
  if (seaLevel !== null && g < seaLevel - SEA_CAST_FREEBOARD_M) return false; // that is the sea
  if (dry?.(x, z)) return false;
  return bakedLakeLevel(lakes, x, z, g) !== null || pondAtLevel(tag, height, seaLevel, x, z, level) || inPond(tag, height, seaLevel, x, z);
}

/** Is the point the kind of water claimed? (`level`: the surface the client rendered there, NaN = none.) */
export function waterIs(kind: Water, river: RiverTest, lakes: ArrayLike<number>, tag: string, height: Height, seaLevel: number | null, x: number, z: number, dry: Dry = null, level = NaN): boolean {
  if (!Number.isFinite(x) || !Number.isFinite(z)) return false;
  if (kind === 'sea') return seaLevel === null ? true : seaWater(height, seaLevel, x, z);
  if (kind === 'river') return river(x, z, RIVER_CAST_SLACK_M);
  return lakeWater(lakes, tag, height, seaLevel, x, z, dry, level);
}

/** The water a cast lands in: the kind claimed when it is true there, else the first of sea, river, lake that is
 *  (a cast by a river mouth claimed 'lake' still fishes the river), else null — no water. */
export function castWater(claimed: Water, river: RiverTest, lakes: ArrayLike<number>, tag: string, height: Height, seaLevel: number | null, x: number, z: number, dry: Dry = null, level = NaN): Water | null {
  if (waterIs(claimed, river, lakes, tag, height, seaLevel, x, z, dry, level)) return claimed;
  for (const k of ['sea', 'river', 'lake'] as const) {
    if (k === claimed || (k === 'sea' && seaLevel === null)) continue;
    if (waterIs(k, river, lakes, tag, height, seaLevel, x, z, dry, level)) return k;
  }
  return null;
}

/** Water a person would stand IN (the invite's spot): a river's own channel, a baked lake over the ground, a pond.
 *  The sea is the caller's (its own freeboard). */
export function standingWater(river: RiverTest, lakes: ArrayLike<number>, tag: string, height: Height, x: number, z: number, dry: Dry = null): boolean {
  if (!Number.isFinite(x) || !Number.isFinite(z)) return false;
  if (river(x, z, RIVER_STAND_SLACK_M)) return true;
  if (dry?.(x, z)) return false;
  const g = height(x, z);
  return bakedLakeLevel(lakes, x, z, g, -0.1) !== null || inPond(tag, height, null, x, z);
}

/** The standing water a building would stand in, NAMED (the placement judge's refusal: "in the river" / "in the
 *  lake" / "in a pond"). */
export type StandingKind = 'river' | 'lake' | 'pond';

/** WHICH standing water is at the point, or null: a river's own channel, a baked lake over its ground, a pond —
 *  `standingWater`'s tests in `standingWater`'s order. Asked by the server's placement judge (lib/structures.ts
 *  placementProblem, the footprint's centre, corners and door approach — 2026-10-01) and, on the same inputs, by the
 *  client's placement map (client/src/lib/placement_water.ts). The sea is the caller's.
 *  THE POND: with `bed` (the drawn bed — the judge and the map pass it) the pond is THE DRAWN POND (`pondStands`, with
 *  `seaLevel` its sea guard); without it, the flood (`inPond`) — and then `standingWater(…) ≡ standingWaterKind(…) !==
 *  null`, the invite's rule (verify:placement-water pins it). */
export function standingWaterKind(river: RiverTest, lakes: ArrayLike<number>, tag: string, height: Height, x: number, z: number, dry: Dry = null, bed: Height | null = null, seaLevel: number | null = null): StandingKind | null {
  if (!Number.isFinite(x) || !Number.isFinite(z)) return null;
  if (river(x, z, RIVER_STAND_SLACK_M)) return 'river';
  if (dry?.(x, z)) return null;
  const g = height(x, z);
  if (bakedLakeLevel(lakes, x, z, g, -0.1) !== null) return 'lake';
  return (bed ? pondStands(`${tag}|bed`, bed, seaLevel, x, z) : inPond(tag, height, null, x, z)) ? 'pond' : null;
}
